// frontend/src/components/production-sequence/sequenceLayout.ts
// Geometría de la tabla de Secuencia, en un solo sitio: la cabecera, las celdas y
// el pie de totales leen de aquí, de modo que los offsets de las columnas
// congeladas no pueden desincronizarse de sus anchos.
import type { CSSProperties } from 'react';

/**
 * Ancho objetivo (px) de cada columna, ajustado al dato que guarda y no a la
 * etiqueta: las etiquetas se han acortado para que no sean ellas las que fijan el
 * suelo. Las columnas de texto largo (descripción, comentarios, motivo) recortan
 * con elipsis y llevan el valor completo en el `title`.
 */
export const COL_W = {
  // ── Información general (las 5 primeras van congeladas a la izquierda) ──
  // Canalón de marcado: viaja con el bloque congelado a propósito. Si scrollase,
  // el usuario perdería de vista qué filas ha marcado justo cuando se va a la
  // derecha a comparar cifras, que es cuando le hacen falta.
  marca: 28,
  position: 56,      // handle de arrastre + hasta 3 dígitos
  ot: 100,           // "26TDPP01747" en monoespaciada
  referencia: 116,
  descripcion: 180,  // texto largo → recorta
  familia: 112,
  subfamilia: 112,
  acabado: 92,       // badge SEMI/RG/TST
  refNueva: 84,      // badge Sí / —
  comentarios: 150,  // editable inline → recorta
  // ── Nivel de servicio ──
  estado: 116,       // badge, el más largo es "Notif. terminado"
  udOT: 92,
  udProd: 108,
  udPte: 100,
  adherencia: 92,    // "100%"
  fPedido: 104,      // dd/mm/aa
  fPlanFin: 112,     // dd/mm/aa + badge de delta de recálculo
  fRealFin: 96,
  motivoDesv: 166,   // marcador (16px) + texto largo → recorta, se abre en tarjeta
  // ── Estado pre-validaciones ──
  calidad: 88,       // checkbox
  granel: 116,       // badge "OK · dd/mm/aa"
  componentes: 164,  // badge "Entre almacenes · dd/mm/aa"
  // ── Productividad ──
  upm: 76,
  udTurno: 100,
  nOp: 88,
} as const;

export type ColKey = keyof typeof COL_W;

/** Las 5 columnas congeladas, en orden. */
export const STICKY_WIDTHS = {
  marca: COL_W.marca,
  pos: COL_W.position,
  ot: COL_W.ot,
  ref: COL_W.referencia,
  desc: COL_W.descripcion,
} as const;

/** Offsets = suma acumulada de los anchos anteriores. */
export const STICKY_OFFSETS = {
  marca: 0,
  pos: STICKY_WIDTHS.marca,
  ot: STICKY_WIDTHS.marca + STICKY_WIDTHS.pos,
  ref: STICKY_WIDTHS.marca + STICKY_WIDTHS.pos + STICKY_WIDTHS.ot,
  desc: STICKY_WIDTHS.marca + STICKY_WIDTHS.pos + STICKY_WIDTHS.ot + STICKY_WIDTHS.ref,
} as const;

export const STICKY_TOTAL = STICKY_OFFSETS.desc + STICKY_WIDTHS.desc;

/** Ancho mínimo de la tabla = suma de todas las columnas. */
export const TABLE_MIN_W = Object.values(COL_W).reduce((s, w) => s + w, 0);

/**
 * Cabecera: el ancho es objetivo y suelo, pero sin techo. Si una etiqueta
 * traducida no cupiese, la columna crece en lugar de recortar el rótulo.
 */
export function thWidth(key: ColKey): CSSProperties {
  return { width: COL_W[key], minWidth: COL_W[key] };
}

/** Celda: el ancho sí es techo — el dato recorta (las celdas llevan overflow oculto). */
export function tdWidth(key: ColKey): CSSProperties {
  return { width: COL_W[key], maxWidth: COL_W[key] };
}
